/**
 * Flags List Component
 * TASK-213: Build Transaction Detail Modal
 *
 * Displays individual compliance flags with severity indicators
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { TransactionFlag } from '../../types/compliance';

interface FlagsListProps {
  /** List of flags to display */
  flags: TransactionFlag[];
  /** Number of flags to show initially before collapse */
  initialDisplay?: number;
}

const FlagsList: React.FC<FlagsListProps> = ({ flags, initialDisplay = 10 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine which flags to show
  const shouldCollapse = flags.length > initialDisplay;
  const displayedFlags = shouldCollapse && !isExpanded
    ? flags.slice(0, initialDisplay)
    : flags;

  // Severity icon mapping
  const getSeverityIcon = (severity: string): string => {
    const icons: Record<string, string> = {
      low: '🟢',
      medium: '🟡',
      high: '🟠',
      critical: '🔴',
    };
    return icons[severity] || '⚪';
  };

  // Severity color mapping
  const getSeverityColors = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-50 border-green-200 text-green-800',
      medium: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      high: 'bg-orange-50 border-orange-200 text-orange-800',
      critical: 'bg-red-50 border-red-200 text-red-800',
    };
    return colors[severity] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  return (
    <div className="space-y-2">
      {displayedFlags.map((flag, index) => (
        <div
          key={index}
          className={`p-3 border rounded-lg ${getSeverityColors(flag.severity)}`}
        >
          <div className="flex items-start gap-2">
            <span className="text-lg flex-shrink-0 mt-0.5">
              {getSeverityIcon(flag.severity)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h5 className="text-sm font-semibold">{flag.description}</h5>
                {flag.regulation && (
                  <span className="text-xs px-2 py-0.5 bg-white/50 rounded-full font-medium whitespace-nowrap">
                    {flag.regulation}
                  </span>
                )}
              </div>
              {flag.detail && (
                <p className="text-xs opacity-90 mt-1">{flag.detail}</p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Show More/Less Button */}
      {shouldCollapse && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show {flags.length - initialDisplay} More Flag{flags.length - initialDisplay !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default FlagsList;
